import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import { Booking, BOOKING_TRANSFORMATION_SERVICE, PRICING_SERVICE } from '@luggagehero/shared/interfaces';
import { SharedBookingService } from '@luggagehero/shared/services/bookings';
import { SharedMomentService } from '@luggagehero/shared/services/moment';
import { SharedNotificationService } from '@luggagehero/shared/services/notification';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';

import { BaseComponent } from '../../../../core';
import { StorageManagerService } from '../../../../services/index';
import { ManageableStorageLocation } from '../../../../services/storage-manager/storage-location';
import { StringUtil } from '../../../../utils';
import { DateUtil } from '../../../../utils/date.util';
import { ListGroup } from '../../../ui';
import { ListItemFactory } from '../../../ui/base/grouped-list/list-item-factory';

const SystemLaunchDate: Date = new Date(2017, 3, 1);
const DefaultPeriodDays = 3;

interface BookingView {
  tab: BookingTab;
  from?: Date;
  to?: Date;
}

interface BookingViewRouteParams {
  view: BookingTab;
  from?: string;
  to?: string;
}

enum BookingTab {
  All = 'All',
  Active = 'Active',
  Incidents = 'Incidents',
}

@Component({ template: '' })
export abstract class BookingsShopBaseComponent extends BaseComponent implements OnInit, OnDestroy {
  @ViewChild('bookingDetailsModal') public bookingDetailsModal: ModalDirective;
  public shop: ManageableStorageLocation;
  public currentView: BookingView = { tab: BookingTab.All };
  public selectedBooking: Booking;
  public error = false;
  public minDate: Date;
  public groupedBookings: ListGroup<Booking>[];

  protected bookingTansformationService = inject(BOOKING_TRANSFORMATION_SERVICE);
  protected momentService = inject(SharedMomentService);
  protected pricingService = inject(PRICING_SERVICE);

  private _isLoading = false;
  private _bookings: Booking[];
  private subscriptions: Subscription[] = [];

  constructor(
    private storageManager: StorageManagerService,
    private bookingService: SharedBookingService,
    private notify: SharedNotificationService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private cd: ChangeDetectorRef,
  ) {
    super();

    this.minDate = SystemLaunchDate;
  }

  public get bookings(): Booking[] {
    return this._bookings;
  }
  public set bookings(value: Booking[]) {
    this._bookings = value;
    this.groupedBookings = this.groupBookings(value);
    this.cd.markForCheck();
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }
  public set isLoading(value: boolean) {
    this._isLoading = value;
    this.cd.markForCheck();
  }

  public get selectedBookingAmount(): number {
    let amount =
      this.selectedBooking.price.final.total -
      (this.selectedBooking.price.final.refund || 0) +
      (this.selectedBooking.price.final.additionalCharge || 0);
    if (this.shop.splitStartupFee === false) {
      amount -= this.selectedBooking.price.final.startupFee;
    }
    return amount;
  }

  public get isShowingToday(): boolean {
    return DateUtil.isSameDate(this.currentView.from, new Date());
  }

  public get currentTab(): BookingTab {
    return this.currentView.tab;
  }

  public get fromDate(): Date {
    return this.currentView.from;
  }

  public get toDate(): Date {
    return this.currentView.to;
  }

  public get isTopDatePaginationVisible(): boolean {
    return [BookingTab.All].includes(this.currentTab);
  }

  public get isBottomDatePaginationVisible(): boolean {
    if (this.isLoading) {
      return false;
    }
    return this.isTopDatePaginationVisible && this.bookings && this.bookings.length > 10;
  }

  public ngOnInit() {
    this.subscriptions.push(
      this.route.params.subscribe((params: { id: string; view: BookingTab; from: string; to: string }) => {
        return void (async () => {
          const shopId = params.id;
          try {
            this.shop = await this.storageManager.getStorageLocation(shopId);
          } catch (err) {
            this.error = true;
            return;
          }

          let tab: BookingTab = BookingTab.All;

          if (params['view'] && Object.values(BookingTab).includes(params['view'])) {
            tab = params['view'];
          }

          // Show default period starting today if no date params are provided
          let from = moment().toDate();
          let to = moment(from).add(DefaultPeriodDays, 'days').toDate();

          if (params['from'] && params['to']) {
            try {
              from = moment(params['from'], 'YYYY-MM-DD', true).toDate();
              to = moment(params['to'], 'YYYY-MM-DD', true).toDate();
            } catch {
              // Ignore
            }
          }

          await this.loadView(tab, from, to);
        })();
      }),
    );
  }

  public ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }
  }

  public isBookingAmountVisible(booking: Booking): boolean {
    if (!AppConfig.IS_STORAGE_MANAGER_BOOKING_AMOUNT_ENABLED) {
      return false;
    }

    if (!this.getBookingAmount(booking)) {
      return false;
    }

    return true;
  }

  public getBookingAmountTooltip(booking: Booking): string {
    return this.isDirectPaymentRequired(booking)
      ? 'YOU_NEED_TO_CHARGE_THIS_AMOUNT'
      : 'YOUR_NET_EARNINGS_FOR_THIS_BOOKING';
  }

  public getBookingAmount(booking: Booking): number {
    if (!['CHECKED_OUT', 'PAID'].includes(booking.status)) {
      return null;
    }
    return this.isDirectPaymentRequired(booking) ? booking.fullBookingAmount : booking.storageLocationBookingAmount;
  }

  public getBookingCurrency(booking: Booking): string {
    if (booking.storageLocationRevenuePlus && !this.isDirectPaymentRequired(booking)) {
      return booking.storageLocationRevenuePlus.currency;
    }
    return booking.price.final?.currency || booking.price.pricing.currency;
  }

  public isDirectPaymentRequired(booking: Booking): boolean {
    return booking.status === 'CHECKED_OUT' || (booking.status === 'PAID' && booking.paidDirectly);
  }

  public showToday() {
    const from = moment().toDate();
    const to = moment(from).add(DefaultPeriodDays, 'days').toDate();

    void this.updateView(this.currentView.tab, from, to);
  }

  public showEarlier() {
    const from = moment(this.currentView.from)
      .subtract(DefaultPeriodDays + 1, 'days')
      .toDate();
    const to = moment(from).add(DefaultPeriodDays, 'days').toDate();

    void this.updateView(this.currentView.tab, from, to);
  }

  public showLater() {
    const from = moment(this.currentView.from)
      .add(DefaultPeriodDays + 1, 'days')
      .toDate();
    const to = moment(from).add(DefaultPeriodDays, 'days').toDate();

    void this.updateView(this.currentView.tab, from, to);
  }

  public async updateView(tab: BookingTab, fromDate: Date, toDate: Date, forceRefresh = false) {
    if (
      tab === this.currentView.tab &&
      DateUtil.isSameDate(fromDate, this.currentView.from) &&
      DateUtil.isSameDate(toDate, this.currentView.to) &&
      !forceRefresh
    ) {
      // No changes
      return;
    }

    let from: string;
    let to: string;

    if (tab === BookingTab.All) {
      from = moment(fromDate).format('YYYY-MM-DD');
      to = moment(toDate).format('YYYY-MM-DD');
    }

    const params: BookingViewRouteParams = { view: tab };

    if (from && to) {
      params.from = from;
      params.to = to;
    }

    const didNavigate = await this.router.navigate(['/storage-locations', this.shop._id, 'bookings', params]);

    if (!didNavigate && forceRefresh) {
      return this.loadView(this.currentTab, this.fromDate, this.toDate, true);
    }
  }

  public showTab(tab: BookingTab, forceRefresh = false) {
    void this.updateView(tab, this.currentView.from, this.currentView.to, forceRefresh);
  }

  public navigateToBooking(booking: Booking) {
    this.bookingService.currentBooking = booking;
    void this.router.navigate(['/storage-locations', booking.shopId, 'bookings', booking._id]);
  }

  private async loadView(tab: BookingTab, from: Date, to: Date, refresh = false) {
    this.isLoading = true;

    //
    // If this is a manual user refresh, add a minimum delay so the user can visually register that something was
    // reloaded even if the API turnaround time is very short
    //
    const minDelay = refresh ? new Promise<void>((resolve) => setTimeout(resolve, 500)) : null;

    this.currentView = {
      tab,
      from: from || this.currentView.from,
      to: to || this.currentView.to,
    };

    let bookings: Booking[];

    try {
      switch (tab) {
        case BookingTab.Incidents:
          bookings = await this.bookingService.getIncidentBookingsForStorageLocation(this.shop._id, refresh);
          break;

        case BookingTab.Active:
          bookings = await this.bookingService.getActiveBookingsForStorageLocation(this.shop._id, refresh);
          break;

        case BookingTab.All:
          bookings = await this.bookingService.getBookingsForStorageLocation(this.shop._id, from, to, refresh);
          break;
      }

      if (bookings) {
        this.bookings = bookings.sort((a, b) => a.period.checkIn.getTime() - b.period.checkIn.getTime());
      }
    } catch (err) {
      this.notify.error(StringUtil.errorToString(err));
    }

    if (minDelay != null) {
      await minDelay;
    }

    this.isLoading = false;
  }

  private groupBookings(value: Booking[]): ListGroup<Booking>[] {
    const groupedBookings: ListGroup<Booking>[] = [];

    for (const booking of value) {
      // Get the group name of the current booking
      const groupName = this.momentService.formatDate(booking.period.checkIn, 'MMM DD, YYYY');

      // Get the current group
      const currentGroup = groupedBookings[groupedBookings.length - 1];

      if (currentGroup && currentGroup.name === groupName) {
        // Add current booking to the current group
        currentGroup.items.push(
          ListItemFactory.createBookingListItem(
            booking,
            this.translate,
            this.bookingTansformationService,
            this.pricingService,
          ),
        );
      } else {
        // Start new group with the current booking
        groupedBookings.push({
          name: groupName,
          icon: 'calendar',
          items: [
            ListItemFactory.createBookingListItem(
              booking,
              this.translate,
              this.bookingTansformationService,
              this.pricingService,
            ),
          ],
        });
      }
    }
    return groupedBookings;
  }
}
